import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { I18n } from '../../../base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../../base/package/util/a11y';
import { getDeviceOrientation } from '../../../base/package/util/device';
import { dispatchCustomEvent } from '../../../base/package/util/event';
import { openDeepLinkInGamification } from '../../../mod-incentive/helper/gamification-launcher';
import { ArrowDirection } from '../../ui-arrow-button/ui-arrow-button';
import '../badge/badge';
import styles from './tournament-bar.css?inline';
import '../../ui-icons/ui-chevron-arrow-icon';

const CName = 'incentive-tournament-bar';

@customElement(CName)
export class IncentiveTournamentBar extends LitElement {
  static readonly styles = unsafeCSS(styles);
  @property({ type: String }) tournamentId = '';
  @property({ type: Number }) position?: number = undefined;
  @property({ type: Number }) points?: number = undefined;
  @property({ type: Number }) joinedAmount?: number = undefined;
  @property({ type: Date }) expirationDate: Date;
  @property({ type: Boolean }) isEnded = false;
  @property({ type: Boolean }) areResultsReady = false;
  @property({ type: Boolean }) isDesktop = false;

  @state() private _layout: 'portrait' | 'landscape' = getDeviceOrientation();
  @state() private _hasActiveNotification = false;
  @state() private _showedAnimationOnce = false;
  @state() private _notification?: { text: string } | null;

  @query('.tournament-bar__notification-wrapper') private _notificationWrapperEl?: HTMLElement;
  @query('.tournament-bar__notification') private _notificationEl?: HTMLElement;

  private _unsubscribeModalActivity?: () => void;

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeModalActivity?.();
    this._unsubscribeModalActivity = undefined;
  }

  protected updated(changed: Map<string, unknown>) {
    if (changed.has('isEnded') || changed.has('areResultsReady')) {
      if ((this.isEnded || this.areResultsReady) && !this._showedAnimationOnce) {
        this._showedAnimationOnce = true;
        this._notification = { text: window.$app.$t.get(I18n.incentive.tournaments.progressBar.ended) };
        this._startNotification();
      }
    }
  }

  render() {
    const missionBarClasses = classMap({
      'tournament-bar': true,
      'tournament-bar--desktop': this.isDesktop,
      'tournament-bar--ended': !this.areResultsReady && this.isEnded,
      'tournament-bar--results-ready': this.areResultsReady,
      'tournament-bar--notification': this._hasActiveNotification,
      'tournament-bar--simple-notification': this.isDesktop || this._layout === 'landscape',
    });

    return html`
      <div class=${missionBarClasses} @click=${this._navigateToTournament}
        tabindex="1"
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._navigateToTournament.bind(this))}
      >
        ${this.areResultsReady || this.isEnded ? this._renderComplete() : this._renderInProgress()}
        ${this._renderNotification()}
      </div>
    `;
  }

  private _renderInProgress() {
    return html`
      ${this._renderPositionAndPoints()}
      <div class="tournament-bar__end">
        ${this._renderPlayerCount()}
        ${this._renderChevron()}
      </div>
    `;
  }

  private _renderPositionAndPoints() {
    const hasData = this.position !== undefined && this.position > 0;

    const positionText = hasData
      ? `${this.position}. ${window.$app.$t.get(I18n.incentive.tournaments.leaderboardTable.position)}`
      : '';
    const pointsText = hasData
      ? `${this.points ?? 0} ${window.$app.$t.get(I18n.incentive.tournaments.leaderboardTable.points)}`
      : '';
    const trophySrc = hasData ? this._getTrophySrc(this.position!) : undefined;

    const classes = classMap({
      'tournament-bar__position': true,
      'tournament-bar__position--hidden': !hasData,
    });

    return html`
      <div class=${classes}>
        ${trophySrc ? html`<img class="tournament-bar__trophy" src=${trophySrc} alt="${positionText}" />` : nothing}
        ${positionText}
        <div class="tournament-bar__points">
          ${pointsText}
        </div>
      </div>
    `;
  }

  private _renderComplete() {
    if (this.areResultsReady) {
      return html`
        <div class="tournament-bar__short-text">
          ${window.$app.$t.get(I18n.incentive.tournaments.progressBar.endedShort)}
        </div>
        <div class="tournament-bar__reward" @animationend=${this._onMissionComplete}>
          ${window.$app.$t.get(I18n.incentive.tournaments.checkYourResult)}
        </div>
      `;
    }

    return html`
      <div class="tournament-bar__short-text">
        ${window.$app.$t.get(I18n.incentive.tournaments.progressBar.endedShort)}
      </div>
      <div class="tournament-bar__reward" @animationend=${this._onMissionComplete}>
        ${window.$app.$t.get(I18n.incentive.tournaments.waitForResult)}
      </div>
    `;
  }

  private _renderPlayerCount() {
    if (!this.joinedAmount) return nothing;

    const value = new Intl.NumberFormat('de-DE').format(this.joinedAmount);
    return html`
      <incentive-badge .variant=${'live'}>
          ${window.$app.$t.get(I18n.incentive.tournaments.players, { value })}
      </incentive-badge>
    `;
  }

  private _renderChevron() {
    return html`
      <ui-chevron-arrow-icon
        class="tournament-bar__chevron"
        .arrowDirection=${ArrowDirection.UP}
        tabindex="-1"
      ></ui-chevron-arrow-icon>
    `;
  }

  private _renderNotification() {
    if (!this._hasActiveNotification || !this._notification?.text) return null;

    return html`
      <div class="tournament-bar__notification-wrapper">
        <div class="tournament-bar__notification" @animationend=${this._onNotificationAnimationEnd}>
          ${this._notification.text}
        </div>
      </div>
    `;
  }

  private async _startNotification() {
    this._hasActiveNotification = true;

    await this.updateComplete;

    const wrapper = this._notificationWrapperEl;
    const notification = this._notificationEl;
    if (!wrapper || !notification) return;

    wrapper.classList.remove('tournament-bar__notification-wrapper--animating');
    notification.classList.remove('tournament-bar__notification--animating');

    // force reflow to restart animation (intentional void access)
    void notification.offsetWidth; // NOSONAR — deliberate reflow trigger to reset CSS animation

    wrapper.classList.add('tournament-bar__notification-wrapper--animating');
    notification.classList.add('tournament-bar__notification--animating');
  }

  private _onNotificationAnimationEnd(e: AnimationEvent) {
    if (e.animationName !== 'notification-puff' && e.animationName !== 'notification-fade') return;
    this._hasActiveNotification = false;
  }

  private _getTrophySrc(position: number): string | undefined {
    const assets = window.staticAssets.gamification;
    switch (position) {
      case 1:
        return assets?.trophyGold;
      case 2:
        return assets?.trophySilver;
      case 3:
        return assets?.trophyBronze;
      default:
        return undefined;
    }
  }

  private _onMissionComplete() {
    dispatchCustomEvent(this, 'missionComplete', {
      bubbles: true,
      composed: true,
    });
  }

  private _navigateToTournament() {
    window.$app.track.gamificationGames({ eventAction: 'inGame_interaction_tournament_bar_expanded' });
    openDeepLinkInGamification('tournament', this.tournamentId);
    this._listenForModalClose();
  }

  private _listenForModalClose() {
    this._unsubscribeModalActivity?.();
    this._unsubscribeModalActivity = window.$app.modal.onModalActivityChange((hasActiveModal) => {
      if (!hasActiveModal) {
        window.$app.track.gamificationGames({ eventAction: 'inGame_interaction_tournament_bar_minimised' });
        this._unsubscribeModalActivity?.();
        this._unsubscribeModalActivity = undefined;
      }
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: IncentiveTournamentBar;
  }
}
